import type { ColumnOptionsSlice, ColumnOptionsSliceEventBus } from '.';
import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import type { ColumnOptions, MarketPhase } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { injectable } from 'src/features/ioc';
import type { StoreAdapter } from 'src/models/StoreAdapter';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

@injectable()
export class ColumnOptionsSliceImpl implements ColumnOptionsSlice {
    #disposableStack = new DisposableStack();
    #columnOptions: Signal<Partial<ColumnOptions>> = signal({});
    #persistOptions: ((options: Partial<ColumnOptions>) => void) | null = null;
    #storeUnsubscribe: (() => void) | null = null;
    #cleanupRegistered = false;

    eventBus: ColumnOptionsSliceEventBus = EventBusBuilder.create()
        .addSignal('widget::columnOptions', () => this.#columnOptions)
        .addEvent('widget::toggleMarketPhase', ({ phase, enabled }) => {
            const currentPhases = this.#columnOptions.value.marketPhases ?? [];
            const newPhases = enabled
                ? currentPhases.includes(phase)
                    ? currentPhases
                    : [...currentPhases, phase]
                : currentPhases.filter((p: MarketPhase) => p !== phase);

            const newOptions: Partial<ColumnOptions> = {
                ...this.#columnOptions.value,
                marketPhases: newPhases.length > 0 ? newPhases : undefined,
            };
            this.#handleUpdate(newOptions);
        })
        .addEvent('widget::setAdjustments', ({ mode }) => {
            const newOptions: Partial<ColumnOptions> = { ...this.#columnOptions.value };
            if (mode !== undefined) {
                newOptions.adjustments = mode;
            } else {
                delete newOptions.adjustments;
            }
            this.#handleUpdate(newOptions);
        })
        .addEvent('widget::setPreheat', ({ value }) => {
            const newOptions: Partial<ColumnOptions> = { ...this.#columnOptions.value };
            if (value !== undefined) {
                newOptions.preheat = value;
            } else {
                delete newOptions.preheat;
            }
            this.#handleUpdate(newOptions);
        })
        .addEvent('widget::clearColumnOptions', () => {
            this.#handleUpdate({});
        })
        .build();

    #handleUpdate(options: Partial<ColumnOptions>): void {
        this.#columnOptions.value = options;
        this.#persistOptions?.(options);
    }

    #syncFromOptions(options: Partial<ColumnOptions>): void {
        const currentOptions = this.#columnOptions.value;
        const marketPhasesChanged = (() => {
            const currentPhases = currentOptions.marketPhases;
            const newPhases = options.marketPhases;
            if (currentPhases === newPhases) return false;
            if (!currentPhases || !newPhases) return true;
            if (currentPhases.length !== newPhases.length) return true;
            const currentSet = new Set(currentPhases);
            return !newPhases.every((phase) => currentSet.has(phase));
        })();
        const optionsChanged =
            currentOptions.preheat !== options.preheat ||
            currentOptions.adjustments !== options.adjustments ||
            marketPhasesChanged;

        if (optionsChanged) {
            this.#columnOptions.value = options;
        }
    }

    init<T extends { columnOptions?: Partial<ColumnOptions> }>(adapter: StoreAdapter<T>): void {
        // Clean up any existing subscription
        if (this.#storeUnsubscribe) {
            this.#storeUnsubscribe();
            this.#storeUnsubscribe = null;
        }

        this.#persistOptions = (options) => adapter.persist({ columnOptions: options } as Partial<T>);

        // Sync initial state
        this.#syncFromOptions(adapter.getState().columnOptions ?? {});

        // Subscribe to state changes
        this.#storeUnsubscribe = adapter.subscribe((state) => {
            this.#syncFromOptions(state.columnOptions ?? {});
        });

        // Register cleanup with disposable stack only once
        if (!this.#cleanupRegistered) {
            this.#cleanupRegistered = true;
            this.#disposableStack.defer(() => {
                if (this.#storeUnsubscribe) {
                    this.#storeUnsubscribe();
                    this.#storeUnsubscribe = null;
                }
            });
        }
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
